(() => {
  const root = document.documentElement;
  const query = new URLSearchParams(window.location.search);
  const staticPreview = query.has('static-preview');
  const previewSection = query.get('preview-section');
  const reduceMotion = staticPreview || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (staticPreview) {
    root.classList.add('static-preview', 'is-ready');
    root.style.scrollBehavior = 'auto';
  }

  if (staticPreview && previewSection) {
    window.setTimeout(() => {
      document.getElementById(previewSection)?.scrollIntoView({ block: 'start' });
    }, 50);
  }

  const revealPage = () => {
    requestAnimationFrame(() => root.classList.add('is-ready'));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', revealPage, { once: true });
  } else {
    revealPage();
  }

  const reveals = [...document.querySelectorAll('.reveal')];
  if ('IntersectionObserver' in window && !reduceMotion) {
    const revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('in-view');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    reveals.forEach((item) => revealObserver.observe(item));
  } else {
    reveals.forEach((item) => item.classList.add('in-view'));
  }

  const header = document.querySelector('[data-header]');
  const hero = document.querySelector('.hero');
  if (header && hero && 'IntersectionObserver' in window) {
    const headerObserver = new IntersectionObserver(([entry]) => {
      header.classList.toggle('is-solid', !entry.isIntersecting || entry.intersectionRatio < 0.12);
    }, { threshold: [0, 0.12] });
    headerObserver.observe(hero);
  }

  const menuToggle = document.querySelector('.menu-toggle');
  const mobileMenu = document.querySelector('.mobile-menu');
  if (menuToggle && mobileMenu) {
    const closeMenu = () => {
      menuToggle.setAttribute('aria-expanded', 'false');
      mobileMenu.classList.remove('is-open');
    };
    menuToggle.addEventListener('click', () => {
      const open = menuToggle.getAttribute('aria-expanded') === 'true';
      menuToggle.setAttribute('aria-expanded', String(!open));
      mobileMenu.classList.toggle('is-open', !open);
    });
    mobileMenu.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
  }

  const stage = document.querySelector('[data-story-stage]');
  const stageName = document.querySelector('[data-stage-name]');
  const chapters = [...document.querySelectorAll('[data-story]')];
  const stageImages = [...document.querySelectorAll('.stage-device')];
  let activeStoryChapter = chapters.find((chapter) => chapter.classList.contains('is-active')) || null;

  const setStory = (mode, activeChapter) => {
    if (!stage || activeChapter === activeStoryChapter) return;
    activeStoryChapter = activeChapter;
    stage.dataset.storyStage = mode;
    chapters.forEach((chapter) => chapter.classList.toggle('is-active', chapter === activeChapter));
    stageImages.forEach((image) => image.classList.toggle('is-active', image.dataset.stageDevice === mode));
    if (stageName) stageName.textContent = { ur: 'UR1000', ju: 'JU2000', ma: 'MA200 Plus' }[mode] || 'UR1000';
  };

  if (chapters.length && 'IntersectionObserver' in window) {
    const updateStory = () => {
      const focusY = window.innerHeight * 0.5;
      const focusedChapter = chapters.reduce((closest, chapter) => {
        const bounds = chapter.getBoundingClientRect();
        const distance = Math.abs(((bounds.top + bounds.bottom) / 2) - focusY);
        return distance < closest.distance ? { chapter, distance } : closest;
      }, { chapter: chapters[0], distance: Number.POSITIVE_INFINITY }).chapter;
      setStory(focusedChapter.dataset.story, focusedChapter);
    };

    const storyObserver = new IntersectionObserver(updateStory, {
      rootMargin: '-47% 0px -47% 0px',
      threshold: 0
    });
    chapters.forEach((chapter) => storyObserver.observe(chapter));
    updateStory();
  }

  const filters = [...document.querySelectorAll('[data-filter]')];
  const productCards = [...document.querySelectorAll('[data-category]')];
  const productCount = document.querySelector('[data-product-count]');

  filters.forEach((filter) => {
    filter.addEventListener('click', () => {
      const value = filter.dataset.filter;
      filters.forEach((button) => {
        const active = button === filter;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });

      let visibleCount = 0;
      productCards.forEach((card) => {
        const show = value === 'all' || card.dataset.category === value;
        card.hidden = !show;
        if (show) visibleCount += 1;
      });
      if (productCount) productCount.textContent = String(visibleCount);
    });
  });

  const canvas = document.querySelector('#diagnostic-canvas');
  if (!canvas || !hero) return;

  const context = canvas.getContext('2d', { alpha: true });
  if (!context) return;

  let width = 0;
  let height = 0;
  let ratio = 1;
  let frame = 0;
  let active = true;
  let pointerX = 0;
  let pointerY = 0;
  let targetX = 0;
  let targetY = 0;

  const resize = () => {
    const bounds = hero.getBoundingClientRect();
    ratio = Math.min(window.devicePixelRatio || 1, 1.6);
    width = Math.max(1, bounds.width);
    height = Math.max(1, bounds.height);
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
  };

  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(hero);
  resize();

  const handlePointer = (event) => {
    const bounds = hero.getBoundingClientRect();
    const localX = event.clientX - bounds.left;
    const localY = event.clientY - bounds.top;
    targetX = localX - width / 2;
    targetY = localY - height / 2;
    hero.style.setProperty('--pointer-x', `${targetX * 0.025}px`);
    hero.style.setProperty('--pointer-y', `${targetY * 0.025}px`);
  };

  if (!reduceMotion) {
    hero.addEventListener('pointermove', handlePointer, { passive: true });
    hero.addEventListener('pointerleave', () => {
      targetX = 0;
      targetY = 0;
      hero.style.setProperty('--pointer-x', '0px');
      hero.style.setProperty('--pointer-y', '0px');
    });
  }

  const drawField = (time) => {
    context.clearRect(0, 0, width, height);
    const phase = reduceMotion ? 0 : time * 0.00028;
    const scrollTop = document.scrollingElement ? document.scrollingElement.scrollTop : 0;
    const scrollShift = Math.min(scrollTop / Math.max(height, 1), 1) * 42;

    pointerX += (targetX - pointerX) * 0.055;
    pointerY += (targetY - pointerY) * 0.055;

    const gradient = context.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, 'rgba(232, 135, 59, 0)');
    gradient.addColorStop(0.34, 'rgba(232, 135, 59, 0.16)');
    gradient.addColorStop(0.72, 'rgba(243, 172, 103, 0.58)');
    gradient.addColorStop(1, 'rgba(232, 135, 59, 0)');

    for (let band = 0; band < 7; band += 1) {
      const base = height * (0.42 + band * 0.055) + scrollShift;
      context.beginPath();
      for (let step = 0; step <= 30; step += 1) {
        const x = (step / 30) * (width + 160) - 80;
        const distance = Math.max(0, 1 - Math.abs(x - (width / 2 + pointerX * 0.22)) / (width * 0.42));
        const bend = pointerY * 0.035 * distance;
        const y = base + Math.sin(x * 0.006 + phase + band * 0.88) * (18 + band * 2.4) + bend;
        if (step === 0) context.moveTo(x, y);
        else context.lineTo(x, y);
      }
      context.strokeStyle = gradient;
      context.lineWidth = band === 3 ? 1.25 : 0.7;
      context.globalAlpha = 0.25 + band * 0.055;
      context.stroke();
    }

    context.globalAlpha = 0.8;
    for (let node = 0; node < 10; node += 1) {
      const x = width * (0.45 + node * 0.058);
      const y = height * (0.49 + (node % 4) * 0.043) + Math.sin(phase * 1.4 + node) * 15;
      context.beginPath();
      context.arc(x, y, node % 3 === 0 ? 2.8 : 1.4, 0, Math.PI * 2);
      context.fillStyle = node % 3 === 0 ? 'rgba(255, 208, 160, 0.9)' : 'rgba(232, 135, 59, 0.58)';
      context.fill();
    }
    context.globalAlpha = 1;
  };

  const animate = (time) => {
    if (!active) return;
    drawField(time);
    frame = requestAnimationFrame(animate);
  };

  if ('IntersectionObserver' in window) {
    const canvasObserver = new IntersectionObserver(([entry]) => {
      active = entry.isIntersecting;
      if (active && !reduceMotion) {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(animate);
      }
    }, { threshold: 0.01 });
    canvasObserver.observe(hero);
  }

  if (reduceMotion) {
    drawField(0);
  } else {
    frame = requestAnimationFrame(animate);
  }
})();
